import { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'

import { CardType } from 'types/deck'

import CardService from 'services/card.service'
import ToastService from 'services/toast.service'

import { useAppSelector } from 'redux/hooks'
import { selectCommanderCards, selectMaindeckCards } from 'redux/deck/selectors'

import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'
import PhatDropdown from 'components/formElements/PhatDropdown'

import styles from './edhRecsForm.module.scss'

type Props = {
  onSubmitted: () => void
  onRequestFailed: (message?: string) => void
  // send back the search object to the parent in case anything wants to be done with it
  onResponseRecieved: (response: CardType[]) => void
  className?: string
  alreadyHasResults?: boolean
  noAutoSearch?: boolean
}

const EdhRecFrom = ({ onSubmitted, onRequestFailed, onResponseRecieved, noAutoSearch }: Props) => {
  const [selectedCardType, setSelectedCardType] = useState('all')
  const [localResults, setLocalResults] = useState(new Array<CardType>())

  const commanders = useAppSelector(selectCommanderCards)
  const maindeck = useAppSelector(selectMaindeckCards)

  const handleSearch = (e?: React.SyntheticEvent, cardType = selectedCardType) => {
    e?.preventDefault()

    // prettier-ignore
    if (!commanders.length) return ToastService.create('Your deck needs a commander before we can get EDHRECs', 'EDHREC Search', 'warning')

    onSubmitted() // let the parent know we're about to submit the search

    let recsSize = '1 - 50'

    if (maindeck.length > 50) recsSize = '51 - 100'
    if (maindeck.length > 100) recsSize = '101 - 150'
    if (maindeck.length > 150) recsSize = '151 - 200'
    if (maindeck.length > 200) recsSize = '200+'

    ReactGA.event({ category: 'Deck', action: `Recs size ${recsSize}` })

    CardService.edhRecs(commanders, maindeck)
      .then(res => {
        setLocalResults(res)
        handleFilterResults(cardType, res)
      })
      .catch(err => {
        console.error(err)

        onRequestFailed(err.error)
      })
  }

  // Passing the local results as an argument here so handleSearch can pass the updated results without rerendering first
  const handleFilterResults = (filter: string, localResults?: CardType[]) => {
    setSelectedCardType(filter)

    if (!localResults || !localResults.length) return handleSearch(undefined, filter)
    if (filter === 'all') return onResponseRecieved(localResults)

    const filteredResults = localResults.filter(card => {
      if (card.types.includes(filter)) return true
      if (card.front?.types.includes(filter)) return true
      if (card.back?.types.includes(filter)) return true

      return false
    })

    onResponseRecieved(filteredResults)
  }

  useEffect(() => {
    if (noAutoSearch) return

    handleSearch()
  }, [])

  return (
    <form onSubmit={handleSearch} className={styles.form}>
      <PhatDropdown
        className={styles.dropdown}
        menuClassName={styles.menu}
        value={selectedCardType}
        options={[
          { label: 'All Card Types', onClick: () => handleFilterResults('all', localResults), id: 'all' },
          { label: 'Artifact', onClick: () => handleFilterResults('Artifact', localResults), id: 'Artifact' },
          { label: 'Creature', onClick: () => handleFilterResults('Creature', localResults), id: 'Creature' },
          { label: 'Enchantment', onClick: () => handleFilterResults('Enchantment', localResults), id: 'Enchantment' },
          { label: 'Instant', onClick: () => handleFilterResults('Instant', localResults), id: 'Instant' },
          { label: 'Land', onClick: () => handleFilterResults('Land', localResults), id: 'Land' },
          {
            label: 'Planeswalker',
            onClick: () => handleFilterResults('Planeswalker', localResults),
            id: 'Planeswalker',
          },
          { label: 'Sorcery', onClick: () => handleFilterResults('Sorcery', localResults), id: 'Sorcery' },
        ]}
      />
      <PhatButton color="green" className={styles.submit}>
        <Icon name="search" /> Search
      </PhatButton>
    </form>
  )
}

export default EdhRecFrom
